import CategoryInfo from "./CategoryInfo";
import FormActions from "./FormActions";
import Loading from "../../core/Loading";
import ParticipantsManager from "./ParticipantsManager";
import PresentationFormFields from "./PresentationFormFields";
import ValidationAlerts from "./ValidationAlerts";
import {
  Box,
  Card,
  CardContent,
  Typography
  } from "@mui/material";
import { CategoryTypes } from "../../../interfaces/enums";
import { PresentationFormProps } from "../types/PresentationFormTypes";
import { useApplicationContext } from "../../../contexts/ApplicationContext";
import { useCategory } from "../../../hooks/useCategory";
import { useEffect, useMemo, useState } from "react";
import { useGroupsContext } from "../../../contexts/GroupsContext";
import { useParams } from "react-router-dom";
import { useParticipantFiltering } from "../hooks/useParticipantFiltering";
import { useParticipantsContext } from "../../../contexts/ParticipantsContext";
import { usePresentationForm } from "../hooks/usePresentationForm";
import { usePresentations } from "../../../hooks/usePresentations";
import { usePresentationValidation } from "../hooks/usePresentationValidation";
/**
 * ============================================================================
 * GROUP PRESENTATION FORM - FORMULARZ PREZENTACJI GRUPOWYCH
 * ============================================================================
 *
 * CEL KOMPONENTU:
 * - Formularz do dodawania/edycji prezentacji grupowych (DUO, TRIO, OTHER)
 * - Grupa zawodników tworzy jedną prezentację
 * - Obsługuje różne typy kategorii z różnymi wymaganiami (nazwa, tytuł)
 * - Filtruje zawodników według ograniczeń kategorii (wiek, płeć)
 * - Wyklucza zawodników już wybranych w innych prezentacjach w tej kategorii
 *
 * ARCHITEKTURA:
 * - Composition Pattern: Łączy różne komponenty w spójny interfejs
 * - Custom Hooks: Używa hooków do zarządzania stanem i logiką
 * - Loading States: Wyświetla loading podczas pobierania danych
 * - Validation: Waliduje dane przed submitowaniem
 *
 * PRZEPŁYW DANYCH:
 * 1. Pobiera dane z kontekstów (application, participants, category)
 * 2. Pobiera istniejące prezentacje w kategorii
 * 3. Filtruje zawodników według ograniczeń kategorii
 * 4. Zarządza stanem formularza (zawodnicy, pola tekstowe, walidacja)
 * 5. Obsługuje submitowanie (grupa zawodników = jedna prezentacja)
 *
 * SPECYFIKA GRUPOWA:
 * - Nazwa prezentacji: DUO/TRIO (automatyczna), OTHER (ręczna)
 * - Tytuł choreografii: może być wymagany (category.isTitleRequired)
 * - Liczba zawodników: między minPresentationSize a maxPresentationSize
 * - Submitowanie: tworzy jedną prezentację z grupą zawodników
 */

export default function GroupPresentationForm({
  initialData,
  onSubmit,
  onCancel,
  editableMode = true,
  registrationBlocked = false,
}: PresentationFormProps) {
  // ============================================================================
  // POBRANIE DANYCH Z KONTEKSTÓW I ROUTINGU
  // ============================================================================

  // Pobierz ID kategorii z URL (np. /presentations/add/:categoryId)
  const { categoryId } = useParams();

  // Pobierz aktualną aplikację z kontekstu (zawiera contestId, applicationId, etc.)
  const { application } = useApplicationContext();

  // Pobierz listę wszystkich zawodników z kontekstu
  const { participants } = useParticipantsContext();

  // Pobierz dane grup z kontekstu
  const { groups, getParticipantsByGroupId } = useGroupsContext();

  // Pobierz dane kategorii na podstawie contestId i categoryId
  const { category } = useCategory(application?.contestId as string, categoryId as string);

  // Pobierz istniejące prezentacje w tej kategorii (potrzebne do filtrowania)
  const { presentations: existingPresentations, isLoading: presentationsLoading } =
    usePresentations({
      categoryId: categoryId as string,
      applicationId: application?.id || "",
    });

  // ============================================================================
  // ZARZĄDZANIE STANEM ŁADOWANIA
  // ============================================================================

  // Sprawdź czy wszystkie wymagane dane są załadowane
  // Prezentacja nie może być utworzona bez kategorii, aplikacji i listy prezentacji
  const isDataLoading = !category || !application || presentationsLoading;

  // ============================================================================
  // PRZEWIJANIE DO GÓRY PO ZAŁADOWANIU
  // ============================================================================

  // Przewiń do góry strony po załadowaniu formularza
  // Zapobiega problemowi, gdy formularz z dużą liczbą zawodników otwiera się w środku okna
  useEffect(() => {
    if (!isDataLoading) {
      // Użyj setTimeout, aby upewnić się, że DOM jest w pełni zrenderowany
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }, 0);
    }
  }, [isDataLoading]);

  // ============================================================================
  // STAN DLA ŹRÓDŁA ZAWODNIKÓW I WYBRANEJ GRUPY
  // ============================================================================

  const [participantSource, setParticipantSource] = useState<"participants" | "group">(
    "participants",
  );
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);

  // ============================================================================
  // INICJALIZACJA HOOKÓW ZARZĄDZAJĄCYCH STANEM I LOGIKĄ
  // ============================================================================

  // Wszystkie hooks muszą być wywoływane przed warunkami (Rules of Hooks)

  // Hook zarządzający stanem formularza (zawodnicy, dane, submitowanie)
  const form = usePresentationForm({
    initialData,
    category,
    onSubmit,
  });

  // Oblicz zawodników do filtrowania na podstawie wybranego źródła
  const participantsForFiltering = useMemo(() => {
    if (participantSource === "group") {
      if (selectedGroupId) {
        return getParticipantsByGroupId(selectedGroupId, participants);
      }
      // Jeśli wybrano "Grupy" ale nie wybrano konkretnej grupy, zwróć pustą listę
      return [];
    }
    return participants;
  }, [participantSource, selectedGroupId, participants, getParticipantsByGroupId]);

  // Hook filtrujący zawodników według ograniczeń kategorii i reguł biznesowych
  const filtering = useParticipantFiltering({
    category,
    existingPresentations,
    showAll: form.state.showAll,
    currentPresentationId: initialData?.id,
    selectedGroupId: participantSource === "group" ? selectedGroupId : null,
    groupParticipantIds:
      participantSource === "group" && selectedGroupId
        ? groups.find((g) => g.id === selectedGroupId)?.participantIds || []
        : null,
  });

  // Hook walidujący dane formularza przed submitowaniem
  const validation = usePresentationValidation({
    participants: form.state.selectedParticipants,
    category,
    formData: form.state.formData,
  });

  // ============================================================================
  // OBSŁUGA STANU ŁADOWANIA
  // ============================================================================

  // Wyświetl loading spinner jeśli dane nie są jeszcze gotowe
  // Zapobiega "jumpy" renderingowi i błędom z undefined data
  if (isDataLoading) {
    return <Loading />;
  }

  // ============================================================================
  // FILTROWANIE ZAWODNIKÓW
  // ============================================================================

  // Filtruj zawodników według ograniczeń kategorii (wiek, płeć, już wybrani, grupa)
  const filteredParticipants = filtering.filterParticipants(participantsForFiltering);

  // Wybierz listę zawodników do wyświetlenia (wszyscy lub przefiltrowani)
  // Jeśli showAll = true, pokaż wszystkich zawodników (ignoruj filtry)
  const availableParticipants = form.state.showAll
    ? participantsForFiltering
    : filteredParticipants;

  // ============================================================================
  // WALIDACJA PRZYCISKU SUBMIT
  // ============================================================================

  // Przycisk submit jest wyłączony jeśli:
  // - Walidacja nie przeszła (błędne dane)
  // - Nie wybrano żadnego zawodnika
  // - Dla kategorii OTHER: brak nazwy prezentacji (DUO/TRIO mają automatyczną)
  // - Tytuł choreografii jest wymagany ale nie został wprowadzony
  const buttonDisabled = Boolean(
    !validation.isValid ||
      form.state.selectedParticipants.length === 0 ||
      (category?.type !== CategoryTypes.DUO &&
        category?.type !== CategoryTypes.TRIO &&
        !(form.state.formData.name || "").trim()) ||
      (category?.isTitleRequired && !(form.state.formData.title || "").trim()),
  );

  // ============================================================================
  // RENDEROWANIE INTERFEJSU UŻYTKOWNIKA
  // ============================================================================

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Card sx={{ border: "1px solid #e0e0e0", borderRadius: 1 }}>
        <CardContent sx={{ p: 3 }}>
          {/* ========================================================================
              NAGŁÓWEK Z PRZYCISKAMI AKCJI
              ======================================================================== */}

          <Box
            sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, color: "#2f2f2f" }}>
              {initialData ? "Edytuj prezentację" : "Informacje o prezentacji i zawodnikach"}
            </Typography>

            {/* Przyciski akcji - zawsze widoczne, ale submit zablokowany gdy rejestracja zamknięta */}
            <FormActions
              onSubmit={form.handleSubmit} // Handler submitowania formularza
              onCancel={onCancel} // Handler anulowania
              disabled={buttonDisabled} // Czy przycisk jest wyłączony
              submitText={form.getSubmitText(!!initialData)} // Tekst przycisku (dynamiczny)
              registrationBlocked={registrationBlocked}
            />
          </Box>

          {/* ========================================================================
              INFORMACJE O KATEGORII
              ======================================================================== */}

          {/* Wyświetl informacje o kategorii (nazwa, ograniczenia wiekowe, płeć) */}
          <CategoryInfo category={category} />

          {/* ========================================================================
              KOMUNIKATY WALIDACJI
              ======================================================================== */}

          {/* Wyświetl komunikaty błędów walidacji (tylko po próbie submitowania) */}
          <ValidationAlerts
            participants={form.state.selectedParticipants} // Lista wybranych zawodników
            category={category} // Dane kategorii (dla walidacji)
            existingCount={filtering.alreadySelectedCount} // Liczba już wybranych zawodników
            formData={form.state.formData} // Dane formularza (dla walidacji pól)
          />

          {/* ========================================================================
              POLA FORMULARZA
              ======================================================================== */}

          {/* Pola tekstowe formularza (nazwa, tytuł) - różne dla różnych typów kategorii */}
          <PresentationFormFields
            formData={form.state.formData} // Dane formularza (nazwa, tytuł)
            onFormDataChange={form.handleFormDataChange} // Handler zmian w polach
            category={category} // Dane kategorii (dla logiki pól)
            editableMode={editableMode} // Tryb edycji czy tylko podgląd
          />

          {/* ========================================================================
              MENEDŻER ZAWODNIKÓW
              ======================================================================== */}

          {/* Komponent do wyboru zawodników z filtrowaniem i walidacją */}
          <ParticipantsManager
            availableParticipants={availableParticipants} // Lista dostępnych zawodników (przefiltrowana)
            selectedParticipants={form.state.selectedParticipants} // Aktualnie wybrani zawodnicy
            onSelectionChange={form.handleParticipantsChange} // Handler zmiany wyboru
            showAll={form.state.showAll} // Flaga trybu "pokaż wszystkich"
            onShowAllChange={form.handleShowAllChange} // Handler przełączania trybu "pokaż wszystkich"
            editableMode={editableMode} // Tryb edycji czy tylko podgląd
            category={category} // Dane kategorii (dla ukrycia przełącznika w SOLO)
            participantSource={participantSource} // Źródło zawodników
            selectedGroupId={selectedGroupId} // ID wybranej grupy
            onParticipantSourceChange={setParticipantSource} // Handler zmiany źródła
            onGroupChange={setSelectedGroupId} // Handler zmiany grupy
          />
        </CardContent>
      </Card>
    </Box>
  );
}
